import { ChangeDetectionStrategy, Component, ElementRef, HostListener, signal, ViewChild, inject, OnInit, DestroyRef } from '@angular/core';
import { BotGreetingsComponent } from '../bot-greetings/bot-greetings.component';
import { BotViewPolicyComponent } from '../bot-view-policy/bot-view-policy/bot-view-policy.component';
import { BotViewPoliciesType, BotViewPolicyType, PolicyChatBotFeedbackReactionValueType } from '../../types/policy.type';
import { EvaluationWidgetComponent } from '../evaluation-widget/evaluation-widget.component';
import { BotSuggestionsComponent } from '@client/app/modules/chat-bot/components/bot-suggestions/bot-suggestions.component';
import { BotSuggestionEntryType } from '@client/app/modules/chat-bot/components/bot-suggestions/bot-suggestions.type';
import { ChatBotPolicyRepoService } from '@client/app/modules/chat-bot/services/chat-bot-policy-repo.service';
import { ChatBotPolicyResultService } from '@client/app/modules/chat-bot/services/chat-bot-policy-result.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { LocalStorageService } from '@monsido/angular-shared-components';
import { NgbTooltipMfeModule } from '@client/mfe/overlay/ngb-tooltip-mfe.module';


type MessageCategory = 'chat' | 'prompt-restart-conversation';

type ChatMessageType = {
    text: string;
    sender: 'bot' | 'user';
    policies?: BotViewPoliciesType['policies'];
    category: MessageCategory;
    promptSelected?: boolean;
};

@Component({
    selector: 'mon-chat-bot',
    imports: [
        BotGreetingsComponent,
        BotViewPolicyComponent,
        EvaluationWidgetComponent,
        BotSuggestionsComponent,
        NgbTooltipMfeModule,
    ],
    templateUrl: './chat-bot.component.html',
    styleUrl: './chat-bot.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    preserveWhitespaces: false,
})
export class ChatBotComponent implements OnInit {
    @ViewChild('userText') userText?: ElementRef<HTMLTextAreaElement>;
    @ViewChild('chatMessages') chatMessages?: ElementRef<HTMLDivElement>;
    @ViewChild('restartConversationButton') restartConversationButton?: ElementRef<HTMLButtonElement>;

    @HostListener('keydown.enter', ['$event'])
    handleKeyDown (event: KeyboardEvent): void {
        if (event.target === this.userText?.nativeElement) {
            event.preventDefault();
            event.stopImmediatePropagation();
            this.sendTextToBot();
        }
    }

    private chatBotPolicyRepoService = inject(ChatBotPolicyRepoService);
    private chatBotPolicyResultService = inject(ChatBotPolicyResultService);
    private localStorageService = inject(LocalStorageService);
    private destroyRef = inject(DestroyRef);

    messages = signal<ChatMessageType[]>([]);
    chatStarted = signal(false);
    hasUserAgreement = signal(false);
    userAuthenticated = signal(false);
    messageBeingProcessed = signal(false);
    isPromptingRestartConversation = signal(false);

    chatId: number | null = null;
    private ChatBotPolicyUserAgreementAcceptedKey = 'chatBotPolicyUserAgreementAccepted';

    ngOnInit (): void {
        const userAgreementAccepted = this.localStorageService.getItem(this.ChatBotPolicyUserAgreementAcceptedKey);
        if (userAgreementAccepted === 'true') {
            this.onUserAgreed();
        }
    }

    async onViewPolicy (policy: BotViewPolicyType): Promise<void> {
        await this.chatBotPolicyResultService.viewPolicyRules(
            policy.name,
            policy.subject,
            policy.rules,
            policy.rules_operator,
        );
    }

    onUserAgreed (): void {
        this.localStorageService.setItem(this.ChatBotPolicyUserAgreementAcceptedKey, 'true');
        this.hasUserAgreement.set(true);
        this.chatBotPolicyRepoService
            .createChat()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(res => {
                this.chatId = res.id;
                this.userAuthenticated.set(true);
            });
    }

    onTextInput (): void {
        const textarea = this.userText?.nativeElement;
        if (!textarea) {
            return;
        }
        textarea.style.setProperty('height', '40px');
        textarea.style.setProperty('height', textarea.scrollHeight + 2 + 'px');
    }

    onTextSubmit (e: Event): void {
        e.preventDefault();
        e.stopImmediatePropagation();
        this.sendTextToBot();
    }

    onSuggestionSelected (suggestion: BotSuggestionEntryType): void {
        if (this.userText?.nativeElement) {
            this.userText.nativeElement.value = suggestion.prompt;
            this.sendTextToBot();
        }
    }

    onFeedbackChoiceSelected (
        [reason, type]: [string, PolicyChatBotFeedbackReactionValueType],
        msgIndex: number,
    ): void {
        if (!this.chatId) {
            return;
        }

        this.chatBotPolicyRepoService
            .addFeedback(
                this.chatId,
                {
                    reason,
                    type,
                    policies_index: this.calculatePolicyGroupIndex(msgIndex),
                },
            )
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe();
    }

    promptRestartConversation () {
        this.isPromptingRestartConversation.set(true);
        this.addMessage({
            sender: 'bot',
            text: 'Are you sure you want to clear this conversation and start a new one? ' +
                'Please note that all previous messages from this conversation will be cleared.',
            category: 'prompt-restart-conversation',
            promptSelected: false,
        });
        setTimeout(() => {
            this.restartConversationButton?.nativeElement?.focus();
        });
    }

    confirmRestartConversation () {
        this.isPromptingRestartConversation.set(false);
        this.messages.set([]);
        this.chatStarted.set(false);
        this.userAuthenticated.set(false);
        this.messageBeingProcessed.set(false);
        this.chatId = null;
        this.onUserAgreed();
    }

    cancelRestartConversation (msgIndex: number) {
        const messages = this.messages();
        messages[msgIndex].promptSelected = true;
        this.messages.set(messages);
        this.isPromptingRestartConversation.set(false);
        this.addMessage({
            sender: 'user',
            text: 'No, don\'t clear this chat',
            category: 'chat',
        });
        setTimeout(() => {
            this.userText?.nativeElement?.focus();
        });
    }

    private calculatePolicyGroupIndex (msgIndex: number): number {
        let result = 0;
        for (let i = 0; i <= msgIndex; i++) {
            const msg = this.messages()[i];
            if (msg.policies && msg.policies.length > 0) {
                result++;
            }
        }

        return result;
    }

    private sendTextToBot (): void {
        if (this.isPromptingRestartConversation()) {
            const messages = this.messages();
            const promptRestartConversationMessage = messages.find(msg => msg.category === 'prompt-restart-conversation' && !msg.promptSelected);
            if (promptRestartConversationMessage) {
                promptRestartConversationMessage.promptSelected = true;
                this.messages.set(messages);
            }
            this.isPromptingRestartConversation.set(false);
        }
        if (this.messageBeingProcessed()) {
            return;
        }
        const textarea = this.userText?.nativeElement;
        if (!textarea) {
            return;
        }
        const message = textarea.value.trim();
        if (!message) {
            return;
        }
        this.chatStarted.set(true);
        this.messageBeingProcessed.set(true);
        textarea.value = '';
        this.onTextInput();

        this.addMessage({
            sender: 'user',
            text: message,
            category: 'chat',
        });

        if (!this.chatId) {
            return;
        }

        this.chatBotPolicyRepoService
            .addMessage(this.chatId, message)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((res: BotViewPoliciesType) => {
                this.processBotResponse(res);
            }, err => {
                this.messageBeingProcessed.set(false);
            });
    }

    private processBotResponse (botResponsePayload: BotViewPoliciesType): void {
        const message: ChatMessageType = {
            sender: 'bot',
            text: botResponsePayload.bot_response,
            category: 'chat',
        };
        if (botResponsePayload.policies) {
            message.policies = botResponsePayload.policies;
        }
        this.addMessage(message);
        this.messageBeingProcessed.set(false);
    }

    private addMessage (message: ChatMessageType): void {
        const messages = this.messages();
        messages.push(message);
        this.messages.set(messages);

        setTimeout(() => {
            const chatMessageElement = this.chatMessages?.nativeElement;
            if (chatMessageElement) {
                chatMessageElement.scrollTo({ left: 0, top: chatMessageElement.scrollHeight, behavior: 'smooth' });
            }
        });
    }

}
